import { useLayoutEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';
import { MarkdownText } from '../utils/conversationRunActivity';
import type { PersonalAgentSnapshot } from './PersonalAgentPage';

type Thread = NonNullable<PersonalAgentSnapshot['thread']>;
type Event = Thread['events'][number];
const WINDOW_SIZE = 200;

function Message({ event }: { event: Event }) {
  const pending = ['queued', 'running', 'waiting_permission'].includes(event.state);
  const failed = event.state === 'halted' || !!event.receipt?.error;
  const text = event.receipt?.text;
  return <article className="personal-chat-turn" data-event-id={event.id}>
    <div className="personal-chat-message personal-chat-message--you">
      <span className="personal-chat-author">You</span>
      <p>{event.request}</p>
    </div>
    <div className={`personal-chat-message personal-chat-message--agent${failed ? ' personal-chat-message--failed' : ''}`}>
      <span className="personal-chat-author">Personal agent</span>
      {text && <MarkdownText text={text} className="personal-chat-markdown" />}
      {failed && <p>I couldn’t finish this reply. Any completed actions are kept.</p>}
      {pending && !text && <p className="personal-chat-progress">{event.state === 'running' ? 'Thinking…' : event.state === 'waiting_permission' ? 'Waiting for your permission…' : 'Message saved. Waiting to reply…'}</p>}
      {(failed || event.effects.length > 0) && <details className="personal-chat-details">
        <summary>{failed ? 'Details' : `${event.effects.length} tool ${event.effects.length === 1 ? 'action' : 'actions'}`}</summary>
        {event.receipt?.error && <p>Error: <code>{event.receipt.error}</code></p>}
        {failed && <p>This request will not be replayed automatically.</p>}
        {event.effects.map(effect => <div key={effect.call_id}>
          <p>{effect.tool}: {effect.result === null ? 'outcome not confirmed' : 'receipt saved'}</p>
          {effect.result !== null && <pre>{JSON.stringify(effect.result, null, 2)}</pre>}
        </div>)}
      </details>}
    </div>
  </article>;
}

export default function PersonalAgentConversation({ thread, apiClient, apiPrefix, request, setRequest, submit, busy, ready }: {
  thread: Thread; apiClient: ApiClient; apiPrefix: string;
  request: string; setRequest: (value: string) => void; submit: () => void;
  busy: boolean; ready: boolean;
}) {
  const [history, setHistory] = useState<{ events: Event[]; hasEarlier: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [away, setAway] = useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  const restore = useRef<{ height: number; top: number } | null>(null);
  const latest = new Map(thread.events.map(event => [event.id, event]));
  // Polling updates statuses in a historical window without displacing it.
  const events = history ? history.events.map(event => latest.get(event.id) ?? event) : thread.events;
  const hasEarlier = history?.hasEarlier ?? thread.has_earlier ?? false;
  useLayoutEffect(() => {
    const node = viewport.current;
    if (!node) return;
    if (restore.current) {
      node.scrollTop = restore.current.top + node.scrollHeight - restore.current.height;
      restore.current = null;
    } else if (follow.current) node.scrollTop = node.scrollHeight;
  }, [events]);
  const jumpToLatest = () => {
    follow.current = true;
    setHistory(null); setAway(false); setHistoryError(null);
    if (viewport.current) viewport.current.scrollTop = viewport.current.scrollHeight;
  };
  const earlier = async () => {
    if (loading || !events.length) return;
    setLoading(true); setHistoryError(null);
    try {
      const page = await apiClient.get<PersonalAgentSnapshot>(`${apiPrefix}?before=${encodeURIComponent(events[0].id)}`);
      const older = page.thread?.events;
      if (!older) throw new Error('history unavailable');
      const node = viewport.current;
      if (node) restore.current = { height: node.scrollHeight, top: node.scrollTop };
      const combined = [...new Map([...older, ...events].map(event => [event.id, event])).values()];
      follow.current = false; setAway(true);
      setHistory({ events: combined.slice(0, WINDOW_SIZE), hasEarlier: page.thread?.has_earlier ?? false });
    } catch { setHistoryError('Earlier messages could not be loaded. Your saved conversation is unchanged.'); }
    finally { setLoading(false); }
  };
  const send = () => {
    if (busy || !ready || !request.trim()) return;
    jumpToLatest(); submit();
  };
  return <section className="personal-agent-chat" aria-label="Persistent personal conversation">
    <div className="personal-chat-toolbar"><h2>Conversation</h2><span>History is saved automatically</span></div>
    <div className="personal-chat-messages" ref={viewport} role="log" aria-label="Messages" aria-live="polite" aria-relevant="additions text"
      onScroll={() => {
        const node = viewport.current;
        if (!node) return;
        const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 96;
        if (!atBottom && !history) setHistory(current => current ?? { events: thread.events, hasEarlier: thread.has_earlier ?? false });
        follow.current = !history && atBottom;
        setAway(!follow.current);
      }}>
      {hasEarlier && <button className="personal-chat-earlier" type="button" disabled={loading} onClick={() => { void earlier(); }}>{loading ? 'Loading…' : 'Load earlier messages'}</button>}
      {historyError && <p role="alert">{historyError}</p>}
      {!events.length && <div className="personal-chat-empty"><h3>What can I help with?</h3><p>Ask about your conversations or tell me what you want to work on.</p></div>}
      {events.map(event => <Message key={event.id} event={event} />)}
    </div>
    <form className="personal-chat-composer" onSubmit={event => { event.preventDefault(); send(); }}>
      {(away || history) && <button className="personal-chat-latest" type="button" onClick={jumpToLatest}>Jump to latest</button>}
      <label className="personal-chat-input-label" htmlFor="personal-agent-request">Message your personal agent</label>
      <div className="personal-chat-input-row">
        <textarea id="personal-agent-request" value={request} maxLength={12288} rows={2} placeholder="Message your personal agent…"
          onChange={event => setRequest(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); }
          }} />
        <button type="submit" disabled={busy || !request.trim() || !ready}>Send request</button>
      </div>
      {thread.paused ? <p>Paused — messages are saved and will be handled after you resume.</p> : <p>Enter to send · Shift+Enter for a new line</p>}
    </form>
  </section>;
}
